// Module-local styles of Inicio: a restrained, utilitarian screen for agents
// in the middle of their work. Every rule below is scoped to Inicio's own
// components; nothing here is global.
//
// - One family (Inter, the dashboard body face) on a fixed px scale
//   (LINE_HEIGHT below). The host's `* { font-size: 12px | 16px;
//   font-family: sans-serif }` (src/index.css) beats inheritance and makes
//   1rem = 12px below 1200px, so every text element sets its own family,
//   size and line height through `text()`.
// - One accent: the dashboard's brand green (designTokens.colors.primary),
//   the one its shared Button and TabPill use, for primary action, selection
//   and state; the v2 page frame (tabs, period control) and the shift panel
//   use the dashboard nav blue, as the mockup. The brand yellow only marks
//   highlights ("Datos de ejemplo").
//   Semantic states are pale tags with readable text.
// - Inicio v2 (approved 2026-10-28): every section is its own card on the
//   gray page ground (`surfaceCard`: 1px border, 18px radius and a faint
//   shadow, as the mockup); pills are for small controls only; no card
//   inside a card.
// - Motion only for state changes: 150 ms colors, a 0.97 press on buttons,
//   no movement under `prefers-reduced-motion`, hover only on fine pointers.
import styled, { css, keyframes } from 'styled-components';

import { designTokens } from '../../../../components/ui/foundation/DesignTokens';
import { Badge } from '../../../../components/ui/components/Badge';
import { toneOf } from '../../../../components/conversationLimit/conversationLimit.style';

const {
  gray, primary, accent, nav, warning,
} = designTokens.colors;

// Contrast on white unless noted: text >= 4.5:1, control boundaries >= 3:1.
export const palette = {
  brand: primary[500], // #2C5F4E, 7.4:1 (white text on it: 7.4:1)
  brandStrong: primary[600], // hover / pressed, text on brand tints (10:1)
  brandTint: primary[50], // selected / tinted grounds
  brandTintStrong: primary[100],
  brandLine: primary[300],
  brandRing: primary.ring,
  highlight: accent[400], // brand yellow, highlights and badges only
  highlightText: accent.text, // 8.5:1 on the yellow
  ink: gray[900],
  text: gray[700],
  secondary: gray[600], // 7.6:1
  muted: gray[500], // 4.8:1 on white, 4.6:1 on gray[50]; never on gray[100]
  line: gray[200],
  lineControl: '#878d96', // 3.3:1: the visible edge of a form field
  subtle: gray[50],
  surface: '#ffffff',
};

// Semantic tags: pale ground, dark text of the same hue (all >= 6:1).
export const tones = {
  brand: { bg: primary[50], text: primary[600] },
  warning: { bg: '#fef3c7', text: '#92400e' },
  danger: { bg: '#fee2e2', text: '#991b1b' },
  neutral: { bg: gray[100], text: gray[700] },
};

const FONT = designTokens.fonts.body;
const LINE_HEIGHT = {
  11: 16, 12: 16, 13: 18, 14: 20, 16: 24, 18: 24, 20: 28, 24: 32, 28: 32, 30: 36,
};

/** Family, size, line height and weight of one text element, in px. */
const text = (size, weight = 400) => css`
  font-family: ${FONT};
  font-size: ${size}px;
  line-height: ${LINE_HEIGHT[size]}px;
  font-weight: ${weight};
`;

export const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';

const focusRing = css`
  &:focus-visible {
    outline: 3px solid ${palette.brand};
    outline-offset: 2px;
  }
`;

// Text fields focus the way the dashboard's Input does: brand edge + ring.
const fieldFocus = css`
  &:focus {
    outline: none;
  }

  &:focus-visible {
    border-color: ${palette.brand};
    box-shadow: 0 0 0 3px ${palette.brandRing};
  }
`;

const onHover = (rules) => css`
  @media (hover: hover) and (pointer: fine) {
    &:hover:not(:disabled) {
      ${rules}
    }
  }
`;

/** One section of Inicio v2: a white card on the gray page ground. */
export const surfaceCard = css`
  box-sizing: border-box;
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: 18px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  min-width: 0;
`;

// The dashboard content area has a fixed height and `overflow: hidden`; each
// screen scrolls inside its own container (same as campaign analytics). The
// gray ground lets every card read as its own section.
export const PageScroller = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  width: 100%;
  overflow-y: auto;
  background: ${gray[100]};
`;

// 1152 px of content (the mockup's column) plus the side padding.
export const Page = styled.div`
  font-family: ${FONT};
  color: ${palette.ink};
  padding: 28px 24px 40px;
  max-width: 1200px;
  margin: 0 auto;
  box-sizing: border-box;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: 600px) {
    padding: 16px 16px 32px;
    gap: 16px;
  }
`;

export const Header = styled.header`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px 16px;
`;

export const HeaderLead = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
`;

// The channel tile next to the greeting of «Mis tareas» (decorative).
export const HeaderIcon = styled.span`
  flex: 0 0 48px;
  height: 48px;
  border-radius: 14px;
  background: #e7f8ee;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 28px;
    height: 28px;
    display: block;
  }
`;

// Today's date over the greeting: written in lower case, shown in upper case,
// so screen readers read words. gray[600]: the mockup's gray[500] is under
// 4.5:1 on the gray ground.
export const HeaderEyebrow = styled.p`
  ${text(12, 700)}
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 0 0 2px;
  color: ${gray[600]};
  overflow-wrap: anywhere;
`;

export const Greeting = styled.h1`
  ${text(28, 800)}
  letter-spacing: -0.01em;
  margin: 0;
  overflow-wrap: anywhere;
  color: ${palette.ink};

  @media (max-width: 600px) {
    ${text(24, 800)}
  }
`;

export const Subtitle = styled.p`
  ${text(14)}
  margin: 4px 0 0;
  color: ${palette.secondary};
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

// The person's own modo-cola capacity (S13c): the chat chip's status colors
// (conversationLimit.style `toneOf`) on Inicio's type scale.
export const CapacityPill = styled.span`
  ${text(13, 700)}
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  border-radius: ${designTokens.radius.full};
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  color: ${(props) => toneOf(props.status).fg};
  background: ${(props) => toneOf(props.status).bg};
  border: 1px solid ${(props) => toneOf(props.status).border};
`;

export const CapacityDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: ${designTokens.radius.full};
  background: currentColor;
`;

// «Configuración» tab (S13b): the explanation card over the shared modo-cola
// panel in its Inicio variant (each part a card of its own).
export const ConfigSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`;

export const ConfigIntro = styled.section`
  ${surfaceCard}
  padding: 22px;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    padding: 18px 16px;
  }
`;

export const ConfigIcon = styled.span`
  flex: 0 0 44px;
  height: 44px;
  border-radius: 12px;
  background: ${nav.activeBg};
  color: ${nav.activeText};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ConfigText = styled.div`
  flex: 1 1 480px;
  min-width: 0;
`;

export const ConfigTitle = styled.h2`
  ${text(18, 700)}
  margin: 0 0 6px;
  color: ${palette.ink};
`;

export const ConfigIntroText = styled.p`
  ${text(14)}
  line-height: 21px;
  margin: 0;
  color: ${gray[700]};
`;

export const FixturesBadge = styled.span`
  ${text(12, 700)}
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: ${designTokens.radius.full};
  background: ${palette.highlight};
  color: ${palette.highlightText};
  white-space: nowrap;
`;

// Underline tabs (mockup v2): the selected one is bold with the nav blue
// line; every tab is a 44 px target.
export const Tabs = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  border-bottom: 1px solid ${palette.line};
`;

export const Tab = styled.button`
  ${text(14, 600)}
  min-height: 44px;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  padding: 0 16px;
  margin: 0 0 -1px;
  border: none;
  border-bottom: 2.5px solid transparent;
  border-radius: 0;
  background: transparent;
  color: ${gray[600]};
  cursor: pointer;
  transition: color 150ms ease, border-color 150ms ease;

  &:focus-visible {
    outline: 3px solid ${nav.activeText};
    outline-offset: -3px;
  }

  ${(props) => props.$active && css`
    font-weight: 700;
    color: ${palette.ink};
    border-bottom-color: ${nav.activeText};
  `}

  ${onHover(css`color: ${palette.ink};`)}

  @media (max-width: 600px) {
    padding: 0 12px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 24px;
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-width: 0;
`;

// Tight inside a group (title to cards: 12px), generous between groups (24px).
export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`;

export const SectionTitle = styled.h2`
  ${text(16, 700)}
  color: ${palette.ink};
  margin: 0;
`;

export const CardList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// The task in progress carries a 2px brand edge (padding drops 1px so its
// content lines up with the other cards) and the "En curso" tag.
export const Card = styled.article`
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;

  ${(props) => props.highlight && css`
    border: 2px solid ${palette.brand};
    padding: 15px;
  `}

  @media (max-width: 600px) {
    padding: 12px;

    ${(props) => props.highlight && css`
      padding: 11px;
    `}
  }
`;

export const CardHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
`;

export const ClientName = styled.span`
  ${text(16, 700)}
  color: ${palette.ink};
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: anywhere;
`;

export const Chips = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  align-items: center;
  max-width: 100%;
`;

const chipTones = {
  humanOnly: tones.warning,
  status: tones.brand,
  neutral: tones.neutral,
  timer: tones.neutral,
  urgent: tones.danger,
};

export const Chip = styled.span`
  ${text(12, 500)}
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: ${designTokens.radius.full};
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  background: ${(props) => (chipTones[props.tone] || chipTones.timer).bg};
  color: ${(props) => (chipTones[props.tone] || chipTones.timer).text};

  /* Not color alone: the urgent countdown is also heavier. */
  ${(props) => props.tone === 'urgent' && css`
    font-weight: 700;
  `}
`;

export const Instruction = styled.p`
  ${text(14, 500)}
  margin: 0;
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

// A tinted block, not a card: no border inside the task card.
export const ContextBox = styled.div`
  ${text(14)}
  background: ${palette.subtle};
  border-radius: 8px;
  padding: 8px 12px;
  color: ${palette.text};
  overflow-wrap: anywhere;
`;

export const ContextLabel = styled.span`
  ${text(12, 500)}
  display: block;
  color: ${palette.secondary};
  margin-bottom: 2px;
`;

export const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

// The dashboard Button's pill, weight and colors (components/ui/components/
// Button.js), without its colored glow or hover lift: a press scale instead.
const buttonBase = css`
  ${text(14, 600)}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 40px;
  padding: 0 16px;
  border-radius: ${designTokens.radius.full};
  border: 1px solid transparent;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 150ms ease, border-color 150ms ease, color 150ms ease, transform 140ms ${EASE_OUT};
  ${focusRing}

  &:active:not(:disabled) {
    transform: scale(0.97);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: background-color 150ms ease, border-color 150ms ease, color 150ms ease;

    &:active:not(:disabled) {
      transform: none;
    }
  }

  @media (max-width: 600px) {
    flex: 1 1 auto;
  }
`;

export const PrimaryButton = styled.button`
  ${buttonBase}
  background: ${palette.brand};
  color: #fff;

  ${onHover(css`background: ${palette.brandStrong};`)}

  &:active:not(:disabled) {
    background: ${palette.brandStrong};
  }
`;

// Second action of the same task ("Cerrar" next to "Atender"): the brand tint.
export const TonalButton = styled.button`
  ${buttonBase}
  background: ${palette.brandTint};
  color: ${palette.brandStrong};
  border-color: ${palette.brandTintStrong};

  ${onHover(css`
    background: ${palette.brandTintStrong};
    border-color: ${designTokens.colors.primary[200]};
  `)}
`;

// The dashboard SoftButton.
export const SecondaryButton = styled.button`
  ${buttonBase}
  background: ${gray[100]};
  color: ${gray[800]};
  border-color: ${palette.line};

  ${onHover(css`
    background: ${palette.surface};
    border-color: ${gray[300]};
  `)}
`;

export const DisabledReason = styled.p`
  ${text(12)}
  margin: 0;
  color: ${palette.secondary};
`;

// Empty and error messages: what happened (or what will appear) and the next step.
export const StateBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: 18px;
  padding: 20px 22px;

  ${(props) => props.$flush && css`
    background: transparent;
    border: none;
    border-radius: 0;
    padding: 0;
  `}
`;

export const StateTitle = styled.p`
  ${text(14, 700)}
  margin: 0;
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

export const StateText = styled.p`
  ${text(14)}
  margin: 0;
  color: ${palette.secondary};
  overflow-wrap: anywhere;
`;

export const StateAction = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

// Placeholders: a slow opacity pulse, no movement, none at all under reduced motion.
const pulse = keyframes`
  from { opacity: 1; }
  to { opacity: 0.5; }
`;

export const Skeleton = styled.span`
  display: block;
  width: ${(props) => props.$w || '100%'};
  max-width: 100%;
  height: ${(props) => props.$h || 12}px;
  border-radius: ${(props) => (props.$pill ? designTokens.radius.full : '4px')};
  background: ${gray[200]};
  animation: ${pulse} 900ms ease-in-out infinite alternate;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const SkeletonStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.$gap || 8}px;
  padding: ${(props) => props.$padding || 0}px;
`;

export const Panel = styled.section`
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`;

export const PanelTitle = styled.h2`
  ${text(16, 700)}
  color: ${palette.ink};
  margin: 0;
`;

// «Tu día» as a card of the first row of «Mis tareas» (mockup v2).
export const DayCard = styled.section`
  ${surfaceCard}
  flex: 1 1 320px;
  padding: 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;

  @media (max-width: 600px) {
    padding: 18px 16px;
  }
`;

export const DayGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 16px;
`;

export const DayFigure = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const DayLabel = styled.span`
  ${text(12, 600)}
  color: ${gray[600]};
`;

export const DayValue = styled.span`
  ${text(24, 800)}
  font-size: 22px;
  line-height: 28px;
  font-variant-numeric: tabular-nums;
  color: ${(props) => (props.$positive ? '#047857' : palette.ink)};
`;

export const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 12px;

  @media (min-width: 601px) and (max-width: 1024px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

// A figure inside a panel: a plain block, no border (no card inside a card).
export const StatBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

// A top-level figure card ("Estadísticas de agentes"), one per KPI.
export const StatCard = styled.div`
  ${surfaceCard}
  flex: 1 1 240px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const StatLabel = styled.span`
  ${(props) => (props.$large ? text(13, 600) : text(12, 500))}
  color: ${palette.secondary};
`;

export const StatValue = styled.span`
  ${(props) => (props.$large ? text(30, 800) : text(24, 700))}
  letter-spacing: ${(props) => (props.$large ? '-0.02em' : '-0.01em')};
  color: ${palette.ink};
  font-variant-numeric: tabular-nums;
  margin: 2px 0;
`;

export const StatHint = styled.span`
  ${text(12)}
  color: ${palette.muted};
`;

export const NoticeList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const Notice = styled.li`
  ${text(14)}
  border-radius: 8px;
  padding: 8px 12px;
  overflow-wrap: anywhere;
  background: ${(props) => (props.tone === 'reminder' ? tones.warning.bg : tones.brand.bg)};
  color: ${(props) => (props.tone === 'reminder' ? tones.warning.text : tones.brand.text)};
`;

export const NoticeMeta = styled.span`
  ${text(12)}
  display: block;
  margin-top: 2px;
`;

// Modals (rendered in a portal, outside Page: every element sizes itself).
export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

export const FieldLabel = styled.label`
  ${text(14, 500)}
  color: ${palette.ink};
`;

export const Legend = styled.legend`
  ${text(14, 500)}
  color: ${palette.ink};
  padding: 0;
  margin-bottom: 8px;
`;

export const Fieldset = styled.fieldset`
  border: none;
  margin: 0 0 16px;
  padding: 0;
  min-width: 0;
`;

export const TextArea = styled.textarea`
  ${text(14)}
  width: 100%;
  box-sizing: border-box;
  min-height: 88px;
  border: 1px solid ${palette.lineControl};
  border-radius: 8px;
  padding: 8px 12px;
  color: ${palette.ink};
  background: ${palette.surface};
  resize: vertical;
  transition: border-color 150ms ease, box-shadow 150ms ease;
  ${fieldFocus}

  &::placeholder {
    color: ${palette.muted};
    opacity: 1;
  }
`;

export const OptionRow = styled.label`
  ${text(14)}
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 44px;
  box-sizing: border-box;
  padding: 10px 12px;
  border: 1px solid ${(props) => (props.checked ? palette.brand : palette.line)};
  background: ${(props) => (props.checked ? palette.brandTint : palette.surface)};
  color: ${palette.ink};
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 8px;
  overflow-wrap: anywhere;
  transition: border-color 150ms ease, background-color 150ms ease;

  &:focus-within {
    outline: 3px solid ${palette.brand};
    outline-offset: 2px;
  }

  input {
    accent-color: ${palette.brand};
    width: 18px;
    height: 18px;
    margin: 0;
    flex-shrink: 0;
  }
`;

export const Warning = styled.p`
  ${text(14, 500)}
  margin: 0 0 16px;
  background: ${tones.warning.bg};
  color: ${tones.warning.text};
  border-radius: 8px;
  padding: 8px 12px;
`;

export const TaskSummary = styled.p`
  ${text(14)}
  margin: 0 0 16px;
  color: ${palette.secondary};
  overflow-wrap: anywhere;
`;

// Team stats: the period control and the note on top, then one card per part.
export const StatsLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
`;

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

export const ToolbarEnd = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  min-width: 0;
`;

// «Periodo» as a segmented pill: 44 px targets, the selected one raised.
export const RangeGroup = styled.div`
  display: inline-flex;
  gap: 4px;
  background: ${gray[200]};
  border-radius: ${designTokens.radius.full};
  padding: 4px;
`;

export const RangeButton = styled.button`
  ${text(13, 700)}
  min-height: 44px;
  padding: 0 16px;
  border: none;
  border-radius: ${designTokens.radius.full};
  cursor: pointer;
  background: ${(props) => (props.$selected ? palette.surface : 'transparent')};
  color: ${(props) => (props.$selected ? palette.ink : gray[600])};
  box-shadow: ${(props) => (props.$selected ? '0 1px 2px rgba(15, 23, 42, 0.12)' : 'none')};
  transition: background-color 150ms ease, color 150ms ease, box-shadow 150ms ease;

  &:focus-visible {
    outline: 3px solid ${nav.activeText};
    outline-offset: 1px;
  }

  ${(props) => !props.$selected && onHover(css`color: ${palette.ink};`)}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

// A new period is loading while the previous one stays on screen.
export const UpdatingNote = styled.span`
  ${text(12, 500)}
  color: ${palette.secondary};
`;

export const StatsBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
  transition: opacity 200ms ease;

  ${(props) => props.$updating && css`
    opacity: 0.6;
  `}
`;

// «Requiere atención»: an amber strip (text 7:1 and more on its ground).
export const ExceptionsBox = styled.section`
  box-sizing: border-box;
  background: ${warning[50]};
  border: 1px solid ${warning[200]};
  border-radius: 18px;
  padding: 16px 20px;
  display: flex;
  align-items: flex-start;
  gap: 8px 14px;
  flex-wrap: wrap;

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    gap: 8px 20px;
    flex-wrap: wrap;
  }

  li {
    ${text(13)}
    color: #78350f;
  }
`;

export const ExceptionsTitle = styled.h2`
  ${text(14, 800)}
  flex: 0 0 auto;
  margin: 0;
  color: #92400e;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const TeamCards = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

// The per-agent table as a card: its title outside the horizontal scroll.
export const TableWrap = styled.section`
  ${surfaceCard}
  padding: 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (max-width: 600px) {
    padding: 18px 16px;
  }
`;

export const TableTitle = styled.h2`
  ${text(16, 700)}
  margin: 0;
  color: ${palette.ink};
`;

export const TableScroll = styled.div`
  overflow-x: auto;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 720px;

  th, td {
    ${text(13)}
    text-align: left;
    padding: 12px;
    border-top: 1px solid ${gray[100]};
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
    color: ${palette.ink};
  }

  th {
    ${text(11, 700)}
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 10px 12px;
    border-top: none;
    background: ${gray[50]};
    color: ${palette.secondary};
  }

  td:first-child {
    font-weight: 600;
  }

  /* The host's global font rule beats inheritance: the name, «pocos datos»
     and «Próximamente» read at the cell's size. */
  td span {
    ${text(13)}
    font-weight: inherit;
  }
`;

// Long agent names end in an ellipsis; the full name is in `title`.
export const AgentName = styled.span`
  display: block;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const FewData = styled.span`
  ${text(14)}
  color: ${palette.muted};
  font-style: italic;
`;

// "Próximamente": a figure that does not exist yet (verified outcome, quality).
export const Soon = styled.span`
  ${text(14, 500)}
  color: ${palette.muted};
  letter-spacing: 0;
`;

export const ReasonList = styled.ul`
  list-style: none;
  margin: 4px 0 0;
  padding: 0;

  li {
    ${text(12)}
    color: ${palette.muted};
    overflow-wrap: anywhere;
  }
`;

export const Footnote = styled.p`
  ${text(12)}
  margin: 0;
  color: ${palette.secondary};
`;

// Nav badge (inside the "Inicio" entry of the main nav): the shared ui-kit
// Badge, shrunk to a counter on the corner of the icon. It lives in the
// dashboard's nav, outside the Inicio screens, so it keeps its nav sizing.
export const NavCount = styled(Badge).attrs({ bg: palette.brand, color: '#fff' })`
  position: absolute;
  top: -4px;
  right: -10px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  justify-content: center;
  box-sizing: border-box;
  font-size: 0.65rem;
  line-height: 18px;
  font-family: ${designTokens.fonts.heading};

  @media (max-width: 900px) {
    font-size: 0.6rem;
    padding: 0 4px;
  }
`;

// Charts ("Estadísticas de agentes" and "Tu semana"), on the criteria of the
// campaign statistics: a hairline-bordered card, a visible title, a short
// muted caption, a recessive solid grid, thin marks with a 4px rounded
// data-end and a 2px surface gap between touching segments. The host's
// global font-size rule beats SVG font-size attributes, so every text
// element here, recharts' included, sets its own size.
export const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 16px;

  @media (min-width: 1025px) {
    grid-template-columns: ${(props) => (props.$wide ? 'minmax(0, 2fr) minmax(0, 1fr)' : 'repeat(2, minmax(0, 1fr))')};
  }

  @media (min-width: 769px) and (max-width: 1024px) {
    grid-template-columns: ${(props) => (props.$wide ? 'minmax(0, 1fr)' : 'repeat(2, minmax(0, 1fr))')};
  }
`;

export const ChartBox = styled.figure`
  margin: 0px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  ${(props) => (props.$framed ? css`
    ${surfaceCard}
    padding: 20px 22px;
  ` : css`
    border-top: 1px solid ${palette.line};
    padding-top: 16px;
  `)}
`;

export const ChartHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 4px 12px;
  margin-bottom: 4px;
`;

export const ChartTitle = styled.h3`
  ${text(14, 700)}
  margin: 0;
  color: ${palette.ink};
`;

export const ChartMeta = styled.span`
  ${text(12)}
  color: ${palette.muted};
  font-variant-numeric: tabular-nums;
`;

export const ChartCaption = styled.p`
  ${text(12)}
  margin: 0 0 12px;
  color: ${palette.muted};
`;

export const ChartEmpty = styled.p`
  ${text(14)}
  margin: 0;
  padding: 16px 0;
  color: ${palette.secondary};
`;

export const LegendList = styled.ul`
  list-style: none;
  margin: 0 0 12px;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
`;

export const LegendItem = styled.li`
  ${text(12)}
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${palette.text};

  span {
    ${text(12)}
  }
`;

// Legends mirror the mark: a rounded square for bars, a short stroke for lines.
export const LegendKey = styled.span`
  flex: 0 0 auto;
  background: ${(props) => props.$color};
  ${(props) => (props.$kind === 'line' ? css`
    width: 14px;
    height: 2px;
    border-radius: 1px;
  ` : css`
    width: 10px;
    height: 10px;
    border-radius: 3px;
  `)}
`;

export const ChartPlot = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;

  .recharts-text,
  .recharts-text tspan {
    font-family: ${FONT};
    font-size: 12px;
  }

  .recharts-tooltip-wrapper p {
    ${text(12)}
  }
`;

// Horizontal HTML bars (per agent, parts of a whole).
export const BarRows = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const BarRow = styled.li`
  ${text(12)}
  position: relative;
  display: grid;
  grid-template-columns: ${(props) => props.$nameWidth}px minmax(0, 1fr) 48px;
  align-items: center;
  gap: 10px;
  border-radius: 4px;
  ${focusRing}
`;

export const BarName = styled.span`
  ${text(12)}
  color: ${palette.ink};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const BarTrack = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 2px;
  height: ${(props) => props.$height || 14}px;
  min-width: 0;
`;

// One bar or stacked segment: square at the baseline (left), 4px rounded data-end.
export const BarSegment = styled.span`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-width: 3px;
  background: ${(props) => props.$color};
  flex: 0 1 auto;
  transition: filter 150ms ease;

  &:last-of-type {
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
  }

  &[data-active='true'] {
    filter: brightness(1.12);
  }
`;

export const SegmentLabel = styled.span`
  ${text(12, 700)}
  line-height: 12px;
  color: ${(props) => props.$ink};
  font-variant-numeric: tabular-nums;
  pointer-events: none;
`;

export const BarValue = styled.span`
  ${text(12, 500)}
  text-align: right;
  color: ${palette.ink};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

// Team median: one 2px solid reference stroke across every row's track. A div,
// so the bar's `:last-of-type` (span) rounded end never lands on it.
export const ReferenceMark = styled.div`
  position: absolute;
  top: -6px;
  bottom: -6px;
  width: 2px;
  margin-left: -1px;
  background: ${(props) => props.$color};
  border-radius: 1px;
  pointer-events: none;
  z-index: 1;
`;

// Sized by its content up to 260px, so a long agent name wraps inside the
// tooltip instead of running past the chart card.
export const HtmlTooltip = styled.div`
  position: absolute;
  bottom: calc(100% + 6px);
  z-index: 3;
  pointer-events: none;
  width: max-content;
  max-width: 260px;
  overflow-wrap: anywhere;

  p, span, strong {
    ${text(12)}
  }

  p:first-child,
  strong {
    font-weight: 700;
  }
`;

export const TooltipRow = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;

  strong {
    color: ${palette.ink};
  }
`;

// The "Cómo terminan" legend doubles as its visible table: one row per part.
export const PartsList = styled.ul`
  list-style: none;
  margin: 16px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const PartsItem = styled.li`
  ${text(14)}
  display: grid;
  grid-template-columns: 10px minmax(0, 1fr) auto 44px;
  align-items: center;
  gap: 8px;
  color: ${palette.text};

  span, strong {
    ${text(14)}
  }

  strong {
    font-weight: 700;
    color: ${palette.ink};
    font-variant-numeric: tabular-nums;
    text-align: right;
  }
`;

export const PartsPct = styled.span`
  color: ${palette.muted};
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

// "pocos datos" inside a bar track, in the table's FewData style.
export const TrackNote = styled(FewData)`
  ${text(12)}
  font-style: italic;
`;

export const SrOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  border: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
`;
